/**
 * cypAdhdPadDataModel.js — the CYP ADHD PAD section tree, in the ADULT
 * assessment-data-model shape.
 *
 * The CYP PAD pages are LITERAL COPIES of the adult PAD pages
 * (9_postAssessmentDiscussion/1_REV, 3_TRT, 4_HLT), which render whatever
 * `dataModel['5_postAssessmentDiscussion']` contains. This constant is that
 * subtree, transcribed from constants/assessment_data_model.py with exactly
 * the CYP deltas agreed on the PAD call + Sai's summary (27-28 Aug):
 *
 *   - Review of Assessment drops "Addressed negative feelings related to a
 *     late diagnosis". That discussion is about an adult receiving a
 *     diagnosis late in life, and it does not belong on the CYP form.
 *     "Validated the patient and/or parent/carer's experience and emotional
 *     response" stays, for a positive and a negative outcome, matching the
 *     adult PAD. The wording names the parent or carer as well as the
 *     patient, because the child is often not the person in the room.
 *   - Strategies & Recommendations (section 2) and Treatment Options
 *     (section 3) carry the confirmed CYP boilerplate line items. Each
 *     prompt has one choice, "Include in patient letter", stored on the
 *     question answer. When ticked, the matching {{IF:}} block in the PAD
 *     document and Patient Report renders the static boilerplate paragraph
 *     for that topic. The key derivation lives in
 *     python-functions/functions/cyp_car/pad_data.py.
 *   - Treatment preferences and the health-checks gate (section 3.3) are
 *     unchanged: that is what unlocks Health Checks.
 *   - Health Checks keeps the adult questions minus the Phoenix-only
 *     controlled-drug prescribing panel.
 *
 * The CypAdhdPadBridge seeds this tree into
 * assessmentStages/postAssessmentDiscussion.data.legacyModel on first open
 * and presents it to the copied pages as a patientDataModel.
 */

/** The single choice on every letter-inclusion prompt. Toggled on or off. */
export const CYP_PAD_LETTER_OPTION = 'Include in patient letter';

/**
 * One letter-inclusion prompt. `questionNumber` is the key the page writes
 * the answer under. `legacyFieldNames` lists field names from earlier
 * versions of this model whose saved tick should carry over to this prompt
 * during reconcile (see `answerForPrompt`).
 */
function letterPrompt(questionNumber, questionTitle, firestoreFieldName, tooltipText, legacyFieldNames) {
  return {
    questionNumber,
    questionTitle,
    questionType: 'selectMultiple',
    options: [CYP_PAD_LETTER_OPTION],
    required: false,
    firestoreFieldName,
    ...(tooltipText ? { tooltipText } : {}),
    ...(legacyFieldNames ? { legacyFieldNames } : {}),
  };
}

export const CYP_PAD_SECTION_MODEL = {
  stageNumber: 5,
  stageTitle: 'Post-Assessment Discussion',
  status: 'notStarted',
  progress: 0,
  sections: {
    1: {
      sectionNumber: 1,
      sectionTitle: 'Review of Assessment',
      sectionType: 'review_of_assessment',
      status: 'notStarted',
      progress: 0,
      route: '/proforma/cyp-adhd/post-assessment-discussion/review-assessment',
      subsections: {
        1: {
          subsectionNumber: 1,
          subsectionTitle: 'Summary of Assessment Findings',
          subsectionTitleDisplay: '1.1 Summary of Assessment Findings',
          questions: {
            1: {
              questionNumber: 1,
              questionTitle: 'Explained the assessment outcome',
              questionType: 'selectMultiple',
              options: ['Discussed'],
              required: false,
              firestoreFieldName: 'explainedAssessmentOutcome',
            },
            2: {
              questionNumber: 2,
              questionTitle: 'Highlighted the benefits of receiving the diagnosis',
              questionType: 'selectMultiple',
              options: ['Discussed'],
              required: false,
              firestoreFieldName: 'highlightedBenefits',
              conditionsToShow: {
                1: {
                  module: '4_diagnosticDecision',
                  sectionTitle: 'Diagnostic Decision',
                  firestoreFieldName: 'diagnosisDecision',
                  valueIsOneOf: ['Yes', 'Fully met', 'Partially met'],
                  valueIsNotOneOf: null,
                },
              },
            },
            // No diagnosis gate. Adult ADHD shows this for a positive and a
            // negative outcome, and the draft only mentions it when the
            // clinician ticks Discussed. The child may not be present, so
            // the label covers the parent or carer as well as the patient.
            3: {
              questionNumber: 3,
              questionTitle: 'Validated the patient and/or parent/carer’s experience and emotional response',
              questionType: 'selectMultiple',
              options: ['Discussed'],
              required: false,
              firestoreFieldName: 'validatedPatientExperiences',
            },
          },
          hasClinicianNotesBox: true,
          // This box is on screen for a positive and a negative ADHD outcome.
          // The child is often not in the room, and when they are, the parent
          // or carer may not want the outcome explained to them. The prompt
          // therefore asks for whoever's reflection was actually captured,
          // rather than assuming it was the child's.
          textBoxSubtitle: 'Capture the patient and/or parent/carer’s reflection as appropriate:',
          notesRequired: false,
          clinicianNotes: null,
        },
        2: {
          subsectionNumber: 2,
          subsectionTitle: 'Patient’s Goals',
          subsectionTitleDisplay: '1.2 Patient’s Goals',
          questions: {},
          hasClinicianNotesBox: true,
          // Shown only after a positive ADHD outcome. The goals are the
          // child's. Whether the parent or carer's account belongs in the
          // note is the clinician's call, so the prompt does not say when.
          textBoxSubtitle: 'Capture details of the patient’s goals, informed by the parent/carer as appropriate:',
          notesRequired: true,
          clinicianNotes: null,
          conditionsToShow: {
            1: {
              module: '4_diagnosticDecision',
              sectionTitle: 'Diagnostic Decision',
              firestoreFieldName: 'diagnosisDecision',
              valueIsOneOf: ['Yes', 'Fully met', 'Partially met'],
              valueIsNotOneOf: null,
            },
          },
          firestoreFieldName: 'patientGoals',
          tooltipText: 'According to NICE guidelines, when developing a shared treatment plan adopt a comprehensive, holistic approach that incorporates the patient’s personal goals which address their psychological, behavioural, and educational needs e.g. to improve school performance, social interactions, educational outcomes or reduce impulsivity.',
        },
      },
    },
    // Confirmed CYP boilerplate line items for Strategies & Recommendations.
    // Each ticked topic renders a static guidance paragraph in the family's
    // report via {{IF:}} keys derived in cyp_car/pad_data.py.
    2: {
      sectionNumber: 2,
      sectionTitle: 'Strategies and Recommendations',
      sectionType: 'discussion_topics',
      status: 'notStarted',
      progress: 0,
      route: '/proforma/cyp-adhd/post-assessment-discussion/strategies-and-recommendations',
      subsections: {
        1: {
          subsectionNumber: 1,
          subsectionTitle: 'Disclosure',
          subsectionTitleDisplay: '2.1 Disclosure',
          subsectionType: 'horizontalOptions',
          questions: {
            1: letterPrompt(1, 'With family and friends', 'disclosureWithFamily', 'Talk about ADHD with trusted people. Parents can highlight strengths and listen without judgement.'),
            2: letterPrompt(2, 'With school', 'disclosureWithSchool', 'Share the report with the SENCO and agree practical adjustments, a named contact and review dates.'),
            // Combined CYP paragraph covering both higher education and
            // employment. The adult PAD has separate rows; those field names
            // are listed as legacyFieldNames so a tick on either old row
            // carries over during reconcile and is read by the Python
            // renderer from stale trees.
            3: letterPrompt(3, 'With higher education or your employer', 'disclosureWithEducationOrEmployer', 'Choosing whether to tell a college, university or employer is a personal decision. Sharing needs may help secure adjustments.', ['disclosureWithEmployer', 'disclosureInEducation']),
          },
        },
        2: {
          subsectionNumber: 2,
          subsectionTitle: 'Lifestyle Factors',
          subsectionTitleDisplay: '2.2 Lifestyle Factors',
          subsectionType: 'horizontalOptions',
          questions: {
            1: letterPrompt(1, 'Physical activity', 'physicalActivity', 'Choose movement the young person enjoys. Children aged 5-18 should aim for 60 minutes of activity daily.'),
            2: letterPrompt(2, 'Diet', 'diet', 'Regular meals and water help maintain energy. Supplements and special diets are not routine ADHD treatments.'),
            3: letterPrompt(3, 'Sleep', 'sleep', 'Keep bedtime consistent. A calming wind-down routine and reduced screens before bed can help.'),
            4: letterPrompt(4, 'Alcohol', 'alcohol', 'Alcohol is safest avoided by children and young people. Discuss how it may affect judgement, sleep and medication.'),
            5: letterPrompt(5, 'Smoking and tobacco', 'smokingAndTobaccoUse', 'Nicotine is not an ADHD treatment. Offer non-judgemental support if they smoke or vape.'),
            6: letterPrompt(6, 'Caffeine', 'caffeine', 'Energy drinks are not recommended for children. Check caffeine in coffee, tea, cola and pre-workout drinks.'),
            7: letterPrompt(7, 'Gambling and gaming behaviour', 'gamblingAndGamingBehaviour', 'Agree realistic boundaries, protect sleep and discuss in-game purchases and gambling-style features.'),
            8: letterPrompt(8, 'Substance use', 'substanceUse', 'Discuss risks openly, including effects on attention, mood and prescribed medicines.'),
            9: letterPrompt(9, 'Financial management', 'financialManagement', 'Practise budgeting in small steps. Turn off one-click purchases and use visual trackers.'),
            10: letterPrompt(10, 'DVLA declaration', 'dvlaDeclaration', 'Applies only to young people who drive or are learning. Discuss whether ADHD or medication affects safe driving.'),
          },
        },
        3: {
          subsectionNumber: 3,
          subsectionTitle: 'Environmental Recommendations',
          subsectionTitleDisplay: '2.3 Environmental Recommendations',
          subsectionType: 'horizontalOptions',
          questions: {
            1: letterPrompt(1, 'Home', 'home', 'Predictable routines, visual planners and short instructions. Notice effort and strengths.'),
            2: letterPrompt(2, 'Education', 'education', 'Share the report with the SENCO and agree an individual support plan. Consider exam access arrangements.'),
            3: letterPrompt(3, 'Employment (if applicable)', 'employment', 'For teenagers in work or apprenticeships: clear tasks, predictable schedules, a quieter workspace and regular feedback.'),
          },
        },
      },
    },
    3: {
      sectionNumber: 3,
      sectionTitle: 'Treatment Options',
      sectionType: 'discussion_topics',
      status: 'notStarted',
      progress: 0,
      route: '/proforma/cyp-adhd/post-assessment-discussion/treatment-options',
      subsections: {
        1: {
          subsectionNumber: 1,
          subsectionTitle: 'Pharmacological Options',
          subsectionTitleDisplay: '3.1 Pharmacological Options',
          subsectionType: 'horizontalOptions',
          questions: {
            1: letterPrompt(1, 'Methylphenidate', 'pharmMethylphenidateHydrochloride', 'First-line stimulant for many children aged 5 and over. Requires monitoring of appetite, sleep, pulse, BP and growth.'),
            2: letterPrompt(2, 'Lisdexamfetamine', 'pharmLisdexamfetamineMesilate', 'Longer-acting stimulant. NICE recommends considering it when methylphenidate has not given enough benefit.'),
            3: letterPrompt(3, 'Atomoxetine', 'pharmAtomoxetine', 'Non-stimulant option when stimulants are unsuitable or not tolerated. Benefits can take several weeks.'),
            4: letterPrompt(4, 'Guanfacine', 'pharmGuanfacine', 'Non-stimulant offered when other options are unsuitable. Can cause sleepiness or low blood pressure; must not be stopped suddenly.'),
          },
        },
        2: {
          subsectionNumber: 2,
          subsectionTitle: 'Non-Pharmacological Options',
          subsectionTitleDisplay: '3.2 Non-Pharmacological Options',
          subsectionType: 'horizontalOptions',
          questions: {
            1: letterPrompt(1, 'ADHD coaching', 'nonPharmAdhdCoaching', 'Practical skills-based support for planning, time management and remembering commitments.'),
            2: letterPrompt(2, 'Cognitive behavioural therapy', 'nonPharmCBT', 'ADHD-adapted CBT can help with unhelpful thinking, emotional regulation and organisation.'),
            3: letterPrompt(3, 'Specialist screening panels and nutritional support', 'nonPharmNutritionalSupport', 'Investigations or dietetic advice when symptoms, growth or dietary restriction suggest a specific concern.'),
            4: letterPrompt(4, 'Psychoeducation programme', 'nonPharmPsychoeducation', 'Understand ADHD as a difference in attention and self-regulation. Explore strengths, challenges and how to ask for support.'),
          },
        },
        3: {
          subsectionNumber: 3,
          subsectionTitle: 'Treatment Preferences',
          subsectionTitleDisplay: '3.3 Treatment Preferences',
          subsectionType: 'verticalOptions',
          questions: {
            1: {
              questionNumber: 1,
              questionTitle: 'Patient’s preferences for treatment',
              questionType: 'selectOne',
              options: [
                'Only pharmacological treatment',
                'Only non-pharmacological treatment',
                'Both pharmacological and non-pharmacological treatment',
                'No treatment',
                'Not yet decided',
              ],
              otherOptions: {
                'No treatment': 'Please provide details about preference of no treatment',
                'Not yet decided': 'Please provide details',
                'Only non-pharmacological treatment': 'Please provide details',
                'Both pharmacological and non-pharmacological treatment': 'Please provide details',
              },
              required: true,
              firestoreFieldName: 'patientTreatmentPreferences',
              tooltipText: "According to NICE guidelines, the patient's preferences and concerns should be recorded in their treatment plan.",
            },
            2: {
              questionNumber: 2,
              questionTitle: 'Carry out health checks now?',
              questionType: 'selectOne',
              options: ['Yes', 'No - defer'],
              conditionsToShow: {
                1: {
                  module: '5_postAssessmentDiscussion',
                  sectionTitle: 'Treatment Options',
                  firestoreFieldName: 'patientTreatmentPreferences',
                  valueIsOneOf: [
                    'Only pharmacological treatment',
                    'Both pharmacological and non-pharmacological treatment',
                    'Not yet decided',
                  ],
                  valueIsNotOneOf: null,
                },
              },
              required: true,
              firestoreFieldName: 'isCarryOutHealthChecksNow',
            },
          },
        },
      },
    },
    4: {
      sectionNumber: 4,
      sectionTitle: 'Health Checks',
      sectionType: 'health_checks',
      status: 'notStarted',
      progress: 0,
      route: '/proforma/cyp-adhd/post-assessment-discussion/health-checks',
      subsections: {
        1: {
          subsectionNumber: 1,
          subsectionTitle: 'Physical Health Review',
          subsectionTitleDisplay: '4.1 Physical Health Review',
          hasInformationBox: false,
          subsectionType: 'verticalOptions',
          // CYP ADHD PAD: no BP/pulse decision questions. Blood pressure and
          // pulse readings will be obtained outside the platform. The Physical
          // Health Review panel in 4_HLT.js still displays height, weight, and
          // BMI from the CI Medical History section with clinician override.
          // The adult PAD has isBloodPressureRangeNormal (Q1) and
          // isPulseNormal (Q2) here.
          questions: {},
        },
        2: {
          subsectionNumber: 2,
          subsectionTitle: 'Potential Contraindications Review',
          subsectionTitleDisplay: '4.2 Potential Contraindications Review',
          hasInformationBox: false,
          subsectionType: 'verticalOptions',
          questions: {
            1: {
              questionNumber: 1,
              questionTitle: 'Potential contraindications to pharmacological treatment?',
              questionType: 'selectOne',
              options: ['Yes', 'No', 'More information required'],
              otherOptions: {
                Yes: 'Please provide details of contraindications',
                'More information required': 'Please provide details of what further information is required',
              },
              firestoreFieldName: 'areContraindicationsPresent',
            },
          },
        },
        3: {
          subsectionNumber: 3,
          subsectionTitle: 'Cardiovascular Risk Assessment',
          subsectionTitleDisplay: '4.3 Cardiovascular Risk Assessment',
          subsectionType: 'verticalOptions',
          questions: {
            1: {
              questionNumber: 1,
              questionTitle: 'Is ECG required?',
              questionType: 'selectOne',
              options: ['Yes', 'No', 'Refer for cardiology opinion'],
              otherOptions: {
                Yes: 'Please provide rationale for ECG being required',
                'Refer for cardiology opinion': 'Please provide details of referral for cardiology opinion',
              },
              firestoreFieldName: 'isECGrequired',
            },
          },
          hasInformationBox: true,
          informationBoxContent: {
            informationBoxTitle: '',
            informationBoxParagraphs: {
              0: { header: 'An ECG is required if any of the following are present', text: '- Family history or medical history of serious cardiac disease' },
              1: { header: '', text: '- Family history of early cardiac death' },
              2: { header: '', text: '- Abnormal findings on cardiac examination' },
              3: { header: '', text: '- Treatment with other medications with potential for QTc prolongation' },
              4: { header: 'Refer for cardiology opinion if any of the following apply:', text: '- History of congenital heart disease or previous cardiac surgery' },
              5: { header: '', text: '- History of sudden death in a first-degree relative under 40 years suggesting a cardiac disease' },
              6: { header: '', text: '- Shortness of breath on exertion compared with peers' },
              7: { header: '', text: '- Fainting on exertion or in response to fright or noise' },
              8: { header: '', text: '- Palpitations that are rapid, regular and start and stop suddenly' },
              9: { header: '', text: '- Chest pain suggesting cardiac origin' },
              10: { header: '', text: '- Signs of heart failure' },
              11: { header: '', text: '- A murmur heard on cardiac examination' },
              12: { header: '', text: '- Blood pressure that is classified as hypertensive for adults' },
            },
          },
        },
        4: {
          subsectionNumber: 4,
          subsectionTitle: 'Outcome of Health Checks',
          subsectionTitleDisplay: '4.4 Outcome of Health Checks',
          subsectionType: 'verticalOptions',
          questions: {
            1: {
              questionNumber: 1,
              questionTitle: 'Is the patient safe to proceed with pharmacological treatment, pending blood pressure and pulse readings?',
              questionType: 'selectOne',
              options: ['Yes', 'No - more information needed or risks identified'],
              otherOptions: {
                'No - more information needed or risks identified': 'Please provide details of decision',
              },
              firestoreFieldName: 'isProceedingAfterFullAssessment',
            },
            2: {
              questionNumber: 2,
              questionTitle: 'Create medication plan now?',
              questionType: 'selectOne',
              options: ['Yes', 'No - refer on to titration', 'No - defer'],
              otherOptions: {
                'No - refer on to titration': 'Please provide details of referral for titration',
                'No - defer': 'Please provide details of deferral',
              },
              conditionsToShow: {
                1: {
                  module: '5_postAssessmentDiscussion',
                  sectionTitle: 'Health Checks',
                  firestoreFieldName: 'isProceedingAfterFullAssessment',
                  valueIsOneOf: ['Yes'],
                  valueIsNotOneOf: null,
                },
              },
              firestoreFieldName: 'isBeginningMedicationNow',
            },
          },
        },
      },
    },
  },
};

/**
 * Signature of the letter prompts (field name, title, choices, tooltip).
 * Ignores saved answers, so a clinician's ticks do not look like a content
 * change. Skips vertical questions such as treatment preferences. Including
 * the tooltip ensures a hint-only change triggers a reconcile so stored
 * trees pick up refreshed helper text.
 */
function letterPromptSignature(section) {
  const rows = [];
  Object.values(section?.subsections || {}).forEach((subsection) => {
    if (subsection?.subsectionType === 'verticalOptions') return;
    Object.values(subsection?.questions || {}).forEach((question) => {
      rows.push([
        question?.firestoreFieldName || '',
        question?.questionTitle || '',
        (question?.options || []).join('|'),
        question?.tooltipText || '',
      ].join('::'));
    });
  });
  return rows.sort().join('\n');
}

/**
 * True when the stored PAD tree already carries the current prompts.
 * An empty Strategies section, the old "Option 1" / "Option 2" placeholders,
 * or stale tooltips returns false so those patients are brought up to date.
 */
export function cypPadLetterContentIsCurrent(model) {
  if (!model?.sections) return false;
  return letterPromptSignature(model.sections['2'])
      === letterPromptSignature(CYP_PAD_SECTION_MODEL.sections['2'])
    && letterPromptSignature(model.sections['3'])
      === letterPromptSignature(CYP_PAD_SECTION_MODEL.sections['3']);
}

/**
 * Copy a saved answer onto the new prompt with the same field name.
 * "Discussed" is dropped: that choice no longer exists, and leaving it in
 * the array would keep a decision the screen can no longer show.
 *
 * When the canonical field has no stored match but the prompt lists
 * `legacyFieldNames`, any tick on one of those old rows carries over to the
 * new combined prompt. This handles the CYP disclosure merge (two adult
 * rows became one CYP row) and any future consolidation.
 */
function answerForPrompt(canonicalQuestion, storedSubsection) {
  const questions = Object.values(storedSubsection?.questions || {});

  // Direct match on the canonical field name.
  const stored = questions.find(
    (question) => question?.firestoreFieldName === canonicalQuestion.firestoreFieldName,
  );
  if (stored?.answer) {
    const value = Array.isArray(stored.answer.value)
      ? stored.answer.value.filter((choice) => choice !== 'Discussed')
      : [];
    return { ...stored.answer, value };
  }

  // Legacy alias match: if any old row was ticked, carry the tick to the
  // new prompt. The subsection is the entire stored section (all
  // subsections), because the legacy rows may have lived in a different
  // subsection. This is called per-subsection in
  // mergeSubsectionKeepingAnswers, but the storedSubsection is the matching
  // subsection from the OLD tree, so a legacy field in the same subsection
  // is found. Cross-subsection aliases are handled by
  // reconcileCypPadLegacyModel searching the whole stored section 2.
  const legacyNames = canonicalQuestion.legacyFieldNames;
  if (Array.isArray(legacyNames) && legacyNames.length > 0) {
    for (const legacyName of legacyNames) {
      const legacyQuestion = questions.find(
        (question) => question?.firestoreFieldName === legacyName,
      );
      if (!legacyQuestion?.answer) continue;
      const legacyValue = Array.isArray(legacyQuestion.answer.value)
        ? legacyQuestion.answer.value.filter((choice) => choice !== 'Discussed')
        : [];
      if (legacyValue.includes(CYP_PAD_LETTER_OPTION)) {
        return { value: [CYP_PAD_LETTER_OPTION] };
      }
    }
  }

  return null;
}

/**
 * Rebuild a subsection from the canonical model, carrying over any saved
 * letter ticks. `allStoredSubsections` is an object of every stored
 * subsection in the section (keyed by subsection ID), so legacy aliases
 * that lived in a different subsection can still be found — the disclosure
 * merge (two rows in old subsection 1 to one combined row in new
 * subsection 1) needs this.
 */
function mergeSubsectionKeepingAnswers(canonicalSubsection, storedSubsection, allStoredSubsections) {
  const questions = {};
  Object.entries(canonicalSubsection.questions || {}).forEach(([questionId, question]) => {
    let answer = answerForPrompt(question, storedSubsection);
    // If the direct subsection had no match and the prompt has legacy
    // aliases, search all stored subsections in this section.
    if (!answer && Array.isArray(question.legacyFieldNames) && question.legacyFieldNames.length > 0) {
      for (const subId of Object.keys(allStoredSubsections || {})) {
        answer = answerForPrompt(question, allStoredSubsections[subId]);
        if (answer) break;
      }
    }
    questions[questionId] = answer ? { ...question, answer } : { ...question };
  });
  return { ...canonicalSubsection, questions };
}

function mergeLetterSection(canonicalSection, storedSection) {
  const stored = storedSection || {};
  const allStoredSubsections = stored.subsections || {};
  const subsections = {};
  Object.entries(canonicalSection.subsections || {}).forEach(([subsectionId, subsection]) => {
    const storedSubsection = allStoredSubsections[subsectionId]
      ?? allStoredSubsections[String(subsectionId)];
    // Treatment preferences (and any other vertical panel) stay exactly as
    // stored, including the health-checks answer that unlocks the next page.
    if (subsection.subsectionType === 'verticalOptions' && storedSubsection) {
      subsections[subsectionId] = storedSubsection;
      return;
    }
    subsections[subsectionId] = mergeSubsectionKeepingAnswers(subsection, storedSubsection, allStoredSubsections);
  });
  return {
    ...canonicalSection,
    status: stored.status || canonicalSection.status,
    progress: stored.progress ?? canonicalSection.progress,
    subsections,
  };
}

/**
 * The PAD tree the pages should render and that an assigned clinician's
 * browser should save.
 *
 * Patients who already opened the PAD have the old tree stored: Strategies
 * has no questions, and non-medication options are "Option 1" and "Option 2".
 * Changing only the seed constant would leave those patients on the broken
 * pages. This rebuilds sections 2 and 3 from the current model and copies
 * any letter answers that already match a field name (the five medications
 * use the same names as before). Saved answers on every other section are
 * left untouched. The Review of Assessment notes subtitles are refreshed
 * from this file, because they are labels and not answers.
 *
 * Pass nothing to get a fresh copy of the current model.
 */
export function reconcileCypPadLegacyModel(stored) {
  const canonical = JSON.parse(JSON.stringify(CYP_PAD_SECTION_MODEL));
  if (!stored) return canonical;
  const next = JSON.parse(JSON.stringify(stored));
  next.sections = next.sections || {};
  next.sections['2'] = mergeLetterSection(canonical.sections['2'], stored.sections?.['2']);
  next.sections['3'] = mergeLetterSection(canonical.sections['3'], stored.sections?.['3']);
  // Section 1 is left as stored so the clinician's notes and ticks survive.
  // The notes subtitles are only labels. Patients who opened the PAD before
  // the wording changed still have the old labels saved on this tree;
  // without this refresh the screen would keep showing them.
  ['1', '2'].forEach((subsectionId) => {
    const storedSubsection = next.sections?.['1']?.subsections?.[subsectionId];
    const currentSubtitle = canonical.sections['1']?.subsections?.[subsectionId]?.textBoxSubtitle;
    if (storedSubsection && currentSubtitle) {
      storedSubsection.textBoxSubtitle = currentSubtitle;
    }
  });
  // Patients seeded before the validation prompt existed have no such
  // question, so the screen would keep hiding a discussion the draft can
  // still be asked to describe. Add any current summary question that is
  // missing, and refresh its wording. A tick already saved on that question
  // stays put.
  const storedSummary = next.sections?.['1']?.subsections?.['1'];
  const canonicalQuestions = canonical.sections['1']?.subsections?.['1']?.questions;
  if (storedSummary && canonicalQuestions) {
    const questions = { ...(storedSummary.questions || {}) };
    Object.entries(canonicalQuestions).forEach(([questionId, question]) => {
      const existingId = Object.keys(questions).find(
        (id) => questions[id]?.firestoreFieldName === question.firestoreFieldName,
      );
      if (!existingId) {
        questions[questionId] = { ...question };
        return;
      }
      const savedAnswer = questions[existingId].answer;
      questions[existingId] = savedAnswer
        ? { ...question, answer: savedAnswer }
        : { ...question };
    });
    storedSummary.questions = questions;
  }
  return next;
}
